import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Avatar } from 'components/ui/avatar';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { useToast } from 'components/ui/use-toast';
import { useAuthContext } from '../context';
import { PROFILE_NAME_MAX_LENGTH, useUpdateProfileName } from '../hooks/useUpdateProfileName';

export function ProfileNameForm() {
  const { t } = useTranslation();
  const {
    state: { user },
  } = useAuthContext();
  const { toast } = useToast();
  const mutation = useUpdateProfileName();
  const [name, setName] = useState(user?.name ?? '');
  const [error, setError] = useState('');

  useEffect(() => {
    setName(user?.name ?? '');
  }, [user?.id, user?.name]);

  const save = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (mutation.isPending) return;
    if (!name.trim()) {
      setError(t('full_name_cannot_be_empty'));
      return;
    }
    if (name.trim().length > PROFILE_NAME_MAX_LENGTH) {
      setError(t('portal.name_too_long'));
      return;
    }
    setError('');
    try {
      await mutation.mutateAsync(name);
      toast.success(t('profile_saved'));
    } catch {
      setError(t('failed_to_save_profile'));
    }
  };

  return (
    <section
      className="max-w-2xl rounded-2xl border border-border bg-background p-5 sm:p-7"
      aria-labelledby="portal-profile-title"
    >
      <div className="flex items-center gap-4 border-b border-border pb-6">
        <Avatar name={user?.name} src={user?.avatarUrl} size="xl" isCurrentUser />
        <div className="min-w-0">
          <h2 id="portal-profile-title" className="text-lg font-semibold">
            {t('profile')}
          </h2>
          <p className="mt-1 break-words text-sm text-muted-foreground">
            {user?.email || user?.phone}
          </p>
        </div>
      </div>
      <form onSubmit={save} className="mt-6 space-y-5" noValidate>
        <div className="space-y-2">
          <Label htmlFor="portal-profile-name">{t('full_name')}</Label>
          <Input
            id="portal-profile-name"
            name="name"
            autoComplete="name"
            value={name}
            maxLength={PROFILE_NAME_MAX_LENGTH}
            disabled={mutation.isPending}
            required
            aria-invalid={!!error}
            aria-describedby={error ? 'portal-profile-error' : 'portal-profile-hint'}
            onChange={(event) => {
              setName(event.target.value);
              setError('');
            }}
          />
          <p id="portal-profile-hint" className="text-xs text-muted-foreground">
            {t('portal.profile_name_hint')}
          </p>
          {error && (
            <p id="portal-profile-error" role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </div>
        <Button type="submit" disabled={mutation.isPending || name.trim() === user?.name}>
          {mutation.isPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
          )}
          {t(mutation.isPending ? 'saving' : 'save_changes')}
        </Button>
      </form>
    </section>
  );
}
